import React, { useMemo } from 'react'
import { useParams } from 'react-router'
import { useErrorHandler } from 'react-error-boundary'
import { useMachine } from '@xstate/react'

import { Feature as FeatureLocation } from 'routing/routes'

import { Feature, FeatureCodec } from 'model/Feature'
import { WIPDataType } from 'model/WIP'
import { metadataMergeAutofill } from 'model/metadata/Metadata'

import { useUser } from 'auth/Auth'

import { fetchResource } from 'api/fetch'
import { metadataFetchAutofill } from 'api/autofill/metadataFetchAutofill'

import { createResourceMachine } from 'shared/resource/resource.machine'
import Loader from 'shared/components/Loader'
import Header from 'shared/components/Header'
import HeaderEditing from 'shared/components/Header/Editing'
import CharactersProvider from 'shared/characters/CharactersProvider'
import { ResourceMachineServiceContext } from 'shared/resource/ResourceMachineProvider'
import { useRefreshLockedTitles } from 'shared/LockedTitles/LockedTitlesProvider'
import { useHasPortrayalsEditingRoles } from 'shared/hooks/useHasRole'
import { NotFoundError, ForbiddenError } from 'shared/errors/ServerError'

import FeatureNode from 'pages/Feature/FeatureNode'

const FeaturePage: React.FC = (): React.ReactElement => {
    const { featureId } = useParams<FeatureLocation>()
    const handleError = useErrorHandler()
    const refreshLockedTitles = useRefreshLockedTitles()
    const canEditPortrayals = useHasPortrayalsEditingRoles()

    const wips: WIPDataType[] = useMemo(() => {
        return [
            'metadata',
            'storylines',
            'relationships',
            ...(canEditPortrayals ? ['characters' as WIPDataType] : []),
        ]
    }, [canEditPortrayals])

    const user = useUser()
    const [state, , service] = useMachine(
        () =>
            createResourceMachine<Feature>({
                resourceId: featureId,
                resourceType: 'feature',
                user,
                wips,
                fetchFn: ctx => fetchResource({ codec: FeatureCodec, ...ctx }),
                handleCriticalError: (_, e: any) => handleError(e.data),
                refreshLockedTitles,
                autofill: {
                    metadata: {
                        fetch: ctx => metadataFetchAutofill(ctx),
                        merge: metadataMergeAutofill,
                    },
                },
            }),
        {
            devTools: true,
        },
    )

    if (state.matches('loading')) {
        return (
            <>
                <Header />
                <Loader center size="normal" />
            </>
        )
    }

    if (state.matches('404')) {
        return (
            <>
                <Header />
                <NotFoundError />
            </>
        )
    }

    if (state.matches('403')) {
        return (
            <>
                <Header />
                <ForbiddenError />
            </>
        )
    }

    return (
        <ResourceMachineServiceContext.Provider value={service}>
            <CharactersProvider
                resourceId={featureId}
                characters={state.context.resource.characters}
            >
                <HeaderEditing />
                <FeatureNode />
            </CharactersProvider>
        </ResourceMachineServiceContext.Provider>
    )
}

export default FeaturePage
